import React, { useState, useEffect } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
} from "framer-motion";
import CountUp from "@/components/wishire/CountUp";

const FACTORS = [
  {
    name: "Resume Signal",
    weight: 25,
    desc: "Recency, relevance, role-skill match, project depth.",
    annotation: "Resume",
  },
  {
    name: "Skill Verification",
    weight: 25,
    desc: "Adaptive assessments + role-specific evaluations.",
    annotation: "Skill Check",
  },
  {
    name: "Interview Performance",
    weight: 30,
    desc: "Voice AI scores clarity, structure, technical depth.",
    annotation: "Voice Interview",
  },
  {
    name: "Career Trajectory",
    weight: 10,
    desc: "Growth, learning velocity, certifications.",
    annotation: null,
  },
  {
    name: "Communication",
    weight: 10,
    desc: "Written + spoken articulation, signal-to-noise.",
    annotation: "Recommendation",
  },
];

const ANNOTATIONS = [
  { label: "Resume", value: "Senior PM · 4y", top: "6%", left: "2%", factorIdx: 0 },
  { label: "Voice Interview", value: "Round 2 · 14:32", bottom: "14%", left: "0%", factorIdx: 2 },
  { label: "Skill Check", value: "System design · 8/10", top: "14%", right: "0%", factorIdx: 1 },
  { label: "Recommendation", value: "Strong hire", bottom: "6%", right: "2%", accent: true, factorIdx: 4 },
];

/* ── Local AnimatedRing ─────────────────────────────────────────────────── */
function AnimatedRing({ value, size = 360, accentColor = "#C2410C" }) {
  const stroke = 16;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const mv = useMotionValue(c);
  const spring = useSpring(mv, { stiffness: 55, damping: 18 });

  useEffect(() => {
    mv.set(c - (value / 100) * c);
  }, [value, c, mv]);

  const gradId = `wsh-score-arc-${size}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="block"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#D97706" />
          <stop offset="50%" stopColor={accentColor} />
          <stop offset="100%" stopColor="#15803D" />
        </linearGradient>
      </defs>
      {/* Track */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.07)"
        strokeWidth={stroke}
      />
      {/* Animated arc */}
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        style={{ strokeDashoffset: spring }}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

/* ── Factor row ─────────────────────────────────────────────────────────── */
function FactorRow({ factor, index, hovered, onHover }) {
  const isActive = hovered === null || hovered === index;
  const isHovered = hovered === index;

  return (
    <motion.div
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
      animate={{ opacity: isActive ? 1 : 0.35 }}
      transition={{ duration: 0.25 }}
      className="group cursor-default py-3 border-b border-stone-100/8 last:border-b-0"
    >
      <div className="flex items-center justify-between text-sm mb-2">
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-stone-500 w-6 shrink-0">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span
            className="transition-colors duration-200"
            style={{ color: isHovered ? "#FCD34D" : "#FAFAF9" }}
          >
            {factor.name}
          </span>
        </div>
        <span
          className="font-mono text-xs transition-colors duration-200 shrink-0 ml-4"
          style={{ color: isHovered ? "#FCD34D" : "rgba(253,251,247,0.45)" }}
        >
          {factor.weight}%
        </span>
      </div>

      {/* Animated weight bar */}
      <div className="ml-9 h-1 bg-stone-100/8 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${factor.weight * 3}%` }}
          transition={{ duration: 0.9, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
          style={{
            background: isHovered
              ? "linear-gradient(90deg, #D97706, #C2410C)"
              : "linear-gradient(90deg, rgba(217,119,6,0.5), rgba(194,65,12,0.5))",
            transition: "background 0.25s",
          }}
        />
      </div>

      <AnimatePresence>
        {isHovered && (
          <motion.p
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: "auto", marginTop: 6 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.22 }}
            className="ml-9 text-stone-400 text-xs leading-relaxed overflow-hidden"
          >
            {factor.desc}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ── Annotation chip ─────────────────────────────────────────────────────── */
function Annotation({ ann, index, hovered }) {
  const isGlowing =
    hovered !== null && ann.factorIdx === hovered;

  return (
    <motion.div
      className="absolute hidden md:block rounded-xl px-3 py-2"
      style={{
        top: ann.top,
        left: ann.left,
        right: ann.right,
        bottom: ann.bottom,
        background: isGlowing
          ? "rgba(217,119,6,0.18)"
          : "rgba(28,25,23,0.65)",
        border: isGlowing
          ? "1px solid rgba(217,119,6,0.5)"
          : "1px solid rgba(255,255,255,0.10)",
        backdropFilter: "blur(12px)",
        transition: "background 0.3s, border 0.3s",
      }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 + index * 0.1, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
    >
      <p
        className="font-mono uppercase tracking-[0.22em]"
        style={{ fontSize: 10, color: "rgba(253,251,247,0.5)" }}
      >
        {ann.label}
      </p>
      <p
        className="text-sm mt-0.5"
        style={{
          color: ann.accent || isGlowing ? "#FCD34D" : "#FDFBF7",
          transition: "color 0.25s",
        }}
      >
        {ann.value}
      </p>
    </motion.div>
  );
}

/* ── Main component ─────────────────────────────────────────────────────── */
export default function WishireScoreSection() {
  const [hovered, setHovered] = useState(null);

  // Subtly shift ring accent when a factor is hovered
  const ringAccent = hovered === 2 ? "#C2410C" : hovered === 0 ? "#D97706" : hovered === 1 ? "#D97706" : "#C2410C";

  return (
    <section id="wishire-score" className="px-3 md:px-6 my-6">
      <div className="relative rounded-[2rem] overflow-hidden bg-stone-900 text-stone-100 wsh-grain">
        {/* Ambient background radials */}
        <div
          className="absolute inset-0 -z-0 opacity-40 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px 400px at 80% 20%, rgba(217,119,6,0.35), transparent 60%), radial-gradient(500px 300px at 10% 80%, rgba(21,128,61,0.25), transparent 60%)",
          }}
        />

        <div className="relative container-x py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

            {/* ── LEFT: headline + interactive factors ── */}
            <div className="lg:col-span-6">
              <motion.span
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6 }}
                className="overline"
                style={{ color: "rgba(253,251,247,0.55)" }}
              >
                The differentiator
              </motion.span>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.75, delay: 0.05 }}
                className="font-display text-4xl md:text-6xl mt-4 leading-[1.05]"
              >
                Meet the{" "}
                <span className="italic text-amber-300">Wishire Score.</span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="mt-6 text-stone-300 text-lg leading-relaxed max-w-xl"
              >
                A single, explainable number — out of 100 — that captures a
                candidate&apos;s readiness for a specific role. Built from five
                signals, weighted by role, refreshed every time you learn
                something new.
              </motion.p>

              {/* Interactive factor list */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-10"
                onMouseLeave={() => setHovered(null)}
              >
                {FACTORS.map((f, i) => (
                  <FactorRow
                    key={f.name}
                    factor={f}
                    index={i}
                    hovered={hovered}
                    onHover={setHovered}
                  />
                ))}
              </motion.div>
            </div>

            {/* ── RIGHT: animated ring + annotations ── */}
            <div className="lg:col-span-6 relative flex items-center justify-center min-h-[420px]">
              {/* Ambient glow behind ring */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <motion.div
                  className="w-[420px] h-[420px] rounded-full"
                  animate={{
                    background: `radial-gradient(closest-side, rgba(217,119,6,${hovered !== null ? 0.32 : 0.2}), transparent 70%)`,
                  }}
                  transition={{ duration: 0.4 }}
                />
              </div>

              {/* Ring + score */}
              <div className="relative">
                <AnimatedRing value={86} size={320} accentColor={ringAccent} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span
                    className="font-mono uppercase tracking-[0.22em]"
                    style={{ fontSize: 10, color: "rgba(253,251,247,0.5)" }}
                  >
                    Wishire
                  </span>
                  <span className="font-display text-7xl leading-none mt-1 text-stone-100">
                    <CountUp value={86} duration={1600} />
                  </span>
                  <span className="text-stone-400 text-sm font-mono mt-1">/ 100</span>
                </div>
              </div>

              {/* Floating annotation chips */}
              {ANNOTATIONS.map((ann, i) => (
                <Annotation
                  key={ann.label}
                  ann={ann}
                  index={i}
                  hovered={hovered}
                />
              ))}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
